import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import Navbar from "../components/Navbar";
import axios from "axios";

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId || !isAuthenticated) {
      setStatus("error");
      return;
    }

    const pollStatus = async () => {
      try {
        // Cookie is automatically sent with request due to withCredentials
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        setPaymentData(response.data);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          // Refresh user data to get updated tier
          window.location.reload();
        } else if (response.data.status === "expired") {
          setStatus("error");
        } else if (pollCount < 5) {
          // Continue polling
          setTimeout(() => setPollCount(c => c + 1), 2000);
        } else {
          setStatus("error");
        }
      } catch (error) {
        if (pollCount < 5) {
          setTimeout(() => setPollCount(c => c + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    pollStatus();
  }, [sessionId, isAuthenticated, pollCount]);

  return (
    <div className="min-h-screen bg-hsl-bg" data-testid="payment-success-page">
      <Navbar onLoginClick={() => {}} onRegisterClick={() => {}} />
      
      <div className="max-w-lg mx-auto px-4 pt-32 pb-16 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-hsl-surface border border-white/10 rounded-2xl p-8"
        >
          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 text-hsl-primary mx-auto mb-6 animate-spin" />
              <h1 className="font-heading text-3xl font-bold uppercase text-white mb-4">
                Processing Payment
              </h1>
              <p className="text-zinc-400">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
              <h1 className="font-heading text-3xl font-bold uppercase text-white mb-4">
                Payment Successful!
              </h1>
              <p className="text-zinc-400 mb-6">
                Welcome to HSL {paymentData?.subscription_tier?.toUpperCase()}! 
                Your account has been upgraded and you now have access to all premium picks.
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/dashboard">
                  <Button className="w-full btn-primary-glow py-4">
                    View Premium Picks <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full bg-white/5 border-white/10 text-white">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
              <h1 className="font-heading text-3xl font-bold uppercase text-white mb-4">
                Payment Issue
              </h1>
              <p className="text-zinc-400 mb-6">
                There was an issue processing your payment. Please try again or contact <a href="mailto:support@hsl.bet" className="text-hsl-gold hover:underline">support@hsl.bet</a> if the problem persists.
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/pricing">
                  <Button className="w-full btn-primary-glow py-4">
                    Try Again
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full bg-white/5 border-white/10 text-white">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
